import React, { useState, useEffect } from 'react';

import { ScrollArea } from "@/components/ui/scroll-area";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

import { Calendar as CalendarIcon } from "lucide-react";

import { useRecoilState } from 'recoil';
import { outletAtom, subAtom } from 'core/config/atoms';

import { motion } from "framer-motion";
import moment from 'moment';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

import ServiceReport from 'services/serviceReport';
import ServicePrinter from 'services/servicePrinter';

import TopBar from 'core/widgets/ui/TopBar';

export default function PanelBooking(props) {
    const [sub] = useRecoilState(subAtom);
    const [outlet] = useRecoilState(outletAtom);

    const [docs, setDocs] = useState([]);

    const [filter, setFilter] = useState({});
    const [record, setRecord] = useState({});

    const [date, setDate] = useState({
        from: moment().startOf('month').toDate(),
        to: moment().toDate(),
    });

    const [tab, setTab] = useState('all');
    const [query, setQuery] = useState('');

    const [pageSize, setPageSize] = useState(10);
    const [pageCurrent, setPageCurrent] = useState(0);
    const [pageTotal, setPageTotal] = useState(0);

    const [isDate, setIsDate] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    const reportService = new ServiceReport();
    const printService = new ServicePrinter();

    const changeTab = (val) => {
        setTab(val);
        setQuery('');
        setDate({
            from: moment().startOf('year').toDate(),
            to: moment().toDate(),
        });
        switch (val) {
            case "all":
                setFilter({});
                break;
            case "booked":
                setFilter({ 'status': 'booked' });
                break;
            case "ongoing":
                setFilter({ 'status': 'ongoing' });
                break;
            case "complete":
                setFilter({ 'status': 'complete' });
                break;
            default:
                break;
        }
    }

    const handleOnChange = (e) => {
        var { value, name } = e.target;
        switch (name) {
            case 'pageSize':
                setPageSize(value);
                break;
            case 'search':
                setQuery(value);
                break;
        }
    }

    const onDateChange = (event) => {
        setIsDate(true);
        setDate(event);
    }

    const openPanel = (doc) => {
        setRecord(doc);
        setIsOpen(true);
    }

    const getPtmStsChip = (status) => {
        switch (status) {
            case 'pending':
                return " bg-red-100 text-red-800";
            case 'partial':
                return " bg-yellow-100 text-yellow-800";
            case 'paid':
                return " bg-green-100 text-green-800";
            default:
                return " bg-gray-100 text-gray-800";
        }
    }

    const getStsChip = (status) => {
        switch (status) {
            case 'booked':
                return " bg-blue-100 text-blue-800";
            case 'ongoing':
                return " bg-yellow-100 text-yellow-800";
            case 'complete':
                return " bg-green-100 text-green-800";
            default:
                return " bg-gray-100 text-gray-800";
        }
    }

    const printOption = (docid) => {
        var body = {
            "id": docid,
            "outlet": outlet.outletid.outletid
        }
        printService.printBooking(body, '').then((res) => {
            toast.success("Booking has sent to print!", { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
        });
    }

    const getTax = (data) => {
        const grandTotal = data.finaltotal;
        let taxAmount = 0;
        if (data.tax > 0) {
            taxAmount = grandTotal - grandTotal / (1 + data.tax / 100);
        }
        return taxAmount.toFixed(2);
    }

    const downloadFile = ({ data, fileName, fileType }) => {
        const blob = new Blob([data], { type: fileType })
        const a = document.createElement('a')
        a.download = fileName
        a.href = window.URL.createObjectURL(blob)
        const clickEvt = new MouseEvent('click', {
            view: window,
            bubbles: true,
            cancelable: true,
        })
        a.dispatchEvent(clickEvt)
        a.remove()
    }

    const exportData = (type) => {
        (async function () {
            let body = {
                "filter": filter,
                "page": 0,
                "size": 0,
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await reportService.getAllBooking(body, '');
            var records = res.data.data;
            let items = [];
            records.forEach((item, index) => {
                items.push({
                    "Booking_ID": item.bookID,
                    "Booking_Name": item.bookName,
                    "Booking_Email": item.bookEmail,
                    "Booking_Phone": item.bookPhone,
                    "Booking_Type": item.bookType,
                    "No_Person": item.nop,
                    "No_Room": item.nor,
                    "Check_In_Time": item.checkIn,
                    "Check_Out_Time": item.checkOut,
                    "Total": item.total,
                    "Extra_Charge": item.extraCharge,
                    "Discount": item.discount,
                    "Tax": item.tax,
                    "Final_Total": item.finaltotal,
                    "Paid": item.paid,
                    "Payment_Status": item.paymentStatus,
                    "status": item.status,
                    "createdAt": item.createdAt,
                });
            });

            if (type === "xlsx") {
                const worksheet = XLSX.utils.json_to_sheet(items);
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, "bookings");
                XLSX.writeFile(workbook, "Bookings.xlsx");
            } else {
                let headers = ['Booking_ID, Booking_Name, Booking_Email, Booking_Phone, Booking_Type, No_Person, No_Room, Check_In_Time, Check_Out_Time, Total, Extra_Charge, Discount, Tax, Final_Total, Paid, Payment_Status, status, createdAt'];
                let recordCsv = items.reduce((acc, record) => {
                    const { Booking_ID, Booking_Name, Booking_Email, Booking_Phone, Booking_Type, No_Person, No_Room, Check_In_Time, Check_Out_Time, Total, Extra_Charge, Discount, Tax, Final_Total, Paid, Payment_Status, status, createdAt } = record
                    acc.push([Booking_ID, Booking_Name, Booking_Email, Booking_Phone, Booking_Type, No_Person, No_Room, Check_In_Time, Check_Out_Time, Total, Extra_Charge, Discount, Tax, Final_Total, Paid, Payment_Status, status, createdAt].join(','))
                    return acc
                }, []);
                downloadFile({
                    data: [...headers, ...recordCsv].join('\n'),
                    fileName: 'product.csv',
                    fileType: 'text/csv',
                });
            }
        })();
    }

    const pageChange = (pageValue) => {
        (async function () {
            let body = {
                "filter": filter,
                "page": pageValue,
                "size": parseInt(pageSize),
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await reportService.getAllBooking(body, '');
            if (res['status']) {
                setPageCurrent(res.data.currentPage);
                setPageTotal(res.data.totalPage);
                setDocs(res.data.data);
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        })();
    }

    const loadData = () => {
        (async function () {
            let body = {
                "filter": filter,
                "page": 1,
                "size": parseInt(pageSize),
                "searchQuery": query,
                "startDate": isDate ? date.from.toISOString() : "",
                "endDate": isDate ? moment(date.to).add(1, 'day').toDate().toISOString() : ""
            };
            var res = await reportService.getAllBooking(body, '');
            if (res['status']) {
                setPageCurrent(res.data.currentPage);
                setPageTotal(res.data.totalPage);
                setDocs(res.data.data);
            } else {
                toast.error('Server error please try again', { position: "top-right", autoClose: 2000, hideProgressBar: false, closeOnClick: true, progress: undefined, theme: "light" });
            }
        })();
    }

    const nextPage = () => {
        if (pageCurrent < pageTotal) {
            pageChange(pageCurrent + 1);
        }
    }

    const prevPage = () => {
        if (pageCurrent > 1) {
            pageChange(pageCurrent - 1);
        }
    }

    useEffect(() => {
        loadData();
    }, []);

    useEffect(() => {
        loadData();
    }, [pageSize, query, date, filter]);

    return (
        <>
            {
                isOpen && <div className='overflow-y-auto overflow-x-hidden z-[100] w-full h-modal md:h-full fixed top-0 left-0 bg-black bg-opacity-70'>
                    <div className='bg-white w-[600px] min-h-screen absolute right-0 py-[20px]'>
                        <ScrollArea>
                            <div className='px-4'>
                                <div className='flex items-center justify-between text-2xl'>
                                    <h1 className='font-sans font-medium text-gray-900 uppercase'>Booking ID #{record.bookID}</h1>
                                    <span onClick={() => {
                                        setIsOpen(false);
                                        setRecord({});
                                    }}><i className="las la-times text-xl text-gray-700 cursor-pointer"></i></span>
                                </div>
                                <div className='flex mt-4'>
                                    <p className='font-sans font-medium'>BooKing Type:
                                        <span className={`ml-4 px-3 inline-flex text-xs leading-5 font-semibold rounded-full uppercase bg-blue-600 text-white`}>
                                            {record.bookType}
                                        </span>
                                    </p>
                                    <p className='ml-8 font-sans font-medium'>Status:
                                        <span className={`ml-4 px-4 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${getStsChip(record.status)}`}>
                                            {record.status}
                                        </span>
                                    </p>
                                </div>
                                <div className='mt-2'>
                                    Timestamp: <b>{moment(record.createdAt).format('DD MMM, yyyy hh:mm:ss A')}</b>
                                </div>
                                <hr className='my-2' />
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-4 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-file-invoice mr-2 text-xl"></i>Booking Summery
                                    </span>
                                </div>
                                <div className='mt-2 flex items-center'>
                                    <p className='text-sm font-bold text-gray-950'>Item Name</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-bold text-gray-950'>Item Unit</p>
                                </div>
                                <hr className='my-2' />
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Booking Start</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{moment(record.checkIn).format('DD MMM, yyyy')}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Booking End</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{moment(record.checkOut).format('DD MMM, yyyy')}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Booking Name</p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{record.bookName}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Email: </p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{record.bookEmail}</p>
                                </div>
                                <div className='flex items-center'>
                                    <p className='text-sm  font-medium text-gray-950'>Phone Number: </p>
                                    <div className='flex-grow'></div>
                                    <p className='text-sm font-medium'>{record.bookPhone}</p>
                                </div>
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-2 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-file-invoice mr-2 text-xl"></i>Payment Summery
                                    </span>
                                </div>
                                <div className="w-full mt-2 space-y-1">
                                    <div className="flex justify-between">
                                        <span className="font-medium text-sm">Subtotal</span>
                                        <span className="font-medium text-sm">₹ {record.total.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="font-medium text-sm">Extra Charges Charges</span>
                                        <span className="font-medium text-sm">₹ {record.extraCharge.toFixed(2)}</span>
                                    </div>
                                    <div className="flex justify-between">
                                        <span className="font-medium text-sm">@ Discount (₹)</span>
                                        <span className="font-medium text-sm">₹ {record.discount.toFixed(2)}</span>
                                    </div>
                                    {
                                        sub.features.isTax && <div className="flex justify-between">
                                            <span className="font-medium text-sm">Tax (₹)</span>
                                            <span className="font-medium text-sm">₹ {getTax(record)}</span>
                                        </div>
                                    }
                                    <div className="border-t-2 mt-2 py-2 flex items-center justify-between">
                                        <span className="font-medium text-lg">Total</span>
                                        <span className="font-medium text-lg">₹ {record.finaltotal.toFixed(2)}</span>
                                    </div>
                                </div>
                                <hr className='my-2' />
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-2 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-file-invoice mr-2 text-xl"></i>Rooms Summery
                                    </span>
                                </div>
                                <div className='flex py-2'>
                                    {
                                        record.roomsAssigned.map((room) => {
                                            return <div className='px-2 py-1 bg-green-600 rounded-md text-sm text-white font-medium'>{room.title}</div>
                                        })
                                    }
                                </div>
                                <hr className='my-2' />
                                <div className='p-2 bg-gray-200 w-full text-sm font-medium rounded mt-2 flex justify-between items-center'>
                                    <span className='flex items-center'>
                                        <i className="las la-file-invoice mr-2 text-xl"></i>Customer Summery
                                    </span>
                                </div>
                                <div className='flex py-2'>
                                    {
                                        record.customerId.length === 0 && <div className='w-full h-[140px] flex items-center justify-center px-2 py-1 rounded-md border border-dashed border-prime text-sm text-prime'>No data found.</div>
                                    }
                                </div>
                                <div className='flex mt-4'>
                                    <div className="py-2 rounded-md shadow-lg text-center bg-prime text-white font-semibold cursor-pointer flex-grow" onClick={() => { printOption(record._id); }}>
                                        PRINT BOOKING
                                    </div>
                                </div>
                            </div>
                        </ScrollArea >
                    </div>
                </div>
            }
            <ScrollArea className="w-full h-full px-[4px]">
                <div className="grid grid-cols-12 grid-gap-2">
                    <div className="col-span-12 mt-2">
                        <div className="intro-y flex items-center justify-between h-10 border-b pb-4">
                            <h2 className="text-2xl font-medium font-serif truncate ml-2 text-ascent">
                                <motion.i whileHover={{ scale: 1.2 }} className="las la-bars cursor-pointer" onClick={() => { props.onToggle(); }}></motion.i> Booking Tally
                            </h2>
                            <TopBar />
                        </div>
                        <div className="w-full mt-2 flex justify-between items-center">
                            <div className='h-10 bg-gray-100 rounded'>
                                <ul className="intro-y flex text-sm font-sans">
                                    <li className={`px-3 h-8 m-1 rounded flex items-center justify-center ${tab === 'all' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('all') }}>All</button></li>
                                    <li className={`px-3 h-8 m-1 rounded flex items-center justify-center ${tab === 'booked' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('booked') }}>Booked</button></li>
                                    <li className={`px-3 h-8 m-1 rounded flex items-center justify-center ${tab === 'ongoing' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('ongoing') }}>Ongoing</button></li>
                                    <li className={`px-3 h-8 m-1 rounded flex items-center justify-center ${tab === 'complete' ? 'text-red-600 font-medium bg-white shadow' : 'text-asent font-medium'}`}><button className='' onClick={() => { changeTab('complete') }}>Complete</button></li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="col-span-12 mt-2">
                    <div className="w-full shadow-md rounded-md border">
                        <table className="min-w-full divide-y divide-gray-200 px-4">
                            <thead className="bg-gray-50">
                                <tr className=''>
                                    <th scope="col" className="border-b border-gray-200" colSpan={8}>
                                        <div className='w-full flex items-center space-x-2 px-2'>
                                            <form className="py-2 flex-grow flex items-center">
                                                <div className='flex'>
                                                    <Popover>
                                                        <PopoverTrigger asChild>
                                                            <Button id="date" variant={"outline"}
                                                                className={cn(
                                                                    "w-[300px] justify-start text-left font-normal",
                                                                    !date && "text-muted-foreground"
                                                                )}
                                                            >
                                                                <CalendarIcon className="mr-2 h-4 w-4" />
                                                                {date?.from ? (
                                                                    date.to ? (
                                                                        <>
                                                                            {moment(date.from).format('MMM DD, yyyy')} -{" "}
                                                                            {moment(date.to).format('MMM DD, yyyy')}
                                                                        </>
                                                                    ) : (
                                                                        moment(date.from).format('MMM DD, yyyy')
                                                                    )
                                                                ) : (
                                                                    <span>Pick a date</span>
                                                                )}
                                                            </Button>
                                                        </PopoverTrigger>
                                                        <PopoverContent className="w-auto p-0" align="start">
                                                            <Calendar
                                                                initialFocus
                                                                mode="range"
                                                                defaultMonth={date?.from}
                                                                selected={date}
                                                                onSelect={onDateChange}
                                                                numberOfMonths={2}
                                                            />
                                                        </PopoverContent>
                                                    </Popover>
                                                </div>
                                                <div className='w-full flex items-center'>
                                                    <div className='w-[80px]'>
                                                        <p>Search :</p>
                                                    </div>
                                                    <input type="text" value={query} onChange={handleOnChange} name='search' placeholder="Search by name." className="w-full h-10 px-4 font-sans text-sm outline-none rounded border" />
                                                </div>
                                            </form>
                                            <div className='flex items-center'>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger>
                                                        <button className="w-full py-1 px-2 h-10 rounded-md bg-prime hover:bg-primeLight text-white mr-4"><i className="las la-file-download mr-2"></i> Export </button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent>
                                                        <div
                                                            className='bg-white shadow-md rounded-md absolute' style={{ top: '0px', left: '-64px' }}>
                                                            <div>
                                                                <ul className='flex flex-col items-start p-2'>
                                                                    <DropdownMenuItem>
                                                                        <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                            exportData('xlsx');
                                                                        }}>
                                                                            <div className='flex items-center text-base'>
                                                                                <p className="text-sm font-serif ml-2 font-normal">Export to .xlsx</p>
                                                                            </div>
                                                                        </li>
                                                                    </DropdownMenuItem>
                                                                    <DropdownMenuItem>
                                                                        <li className="w-40 cursor-pointer p-2 rounded-md" onClick={() => {
                                                                            exportData('csv');
                                                                        }}>
                                                                            <div className='flex items-center text-base'>
                                                                                <p className="text-sm font-serif ml-2 font-normal">Export to .csv</p>
                                                                            </div>
                                                                        </li>
                                                                    </DropdownMenuItem>
                                                                </ul>
                                                            </div>
                                                        </div>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </div>
                                        </div>
                                    </th>
                                </tr>
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        Booking ID
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        Customer Details
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        Check-In
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        Check-Out
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        Booking Type
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        Payment Status
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        Status
                                    </th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer">
                                        Created At
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {
                                    docs.length !== 0 && docs.map((order) => {
                                        return <tr className="hover:bg-gray-100" key={order._id}>
                                            <td className="px-6 py-4 whitespace-nowrap cursor-pointer">
                                                <div className="text-sm font-bold text-gray-900 cursor-pointer" onClick={() => { openPanel(order) }}>
                                                    <div className='flex items-center text-prime'>
                                                        {order.bookID}
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm font-bold text-gray-900">
                                                    {order.bookName}<br />
                                                    <span className='text-xs text-gray-700'>Email: {order.bookEmail}</span><br />
                                                    <span className='text-xs text-gray-700'>Phone: {order.bookPhone}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-blue-600">
                                                    {moment(order.checkIn).format('DD MMM, yyyy hh:mm:ss A')}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-blue-600">
                                                    {moment(order.checkOut).format('DD MMM, yyyy hh:mm:ss A')}
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-600 text-white">
                                                    {order.bookType.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${getPtmStsChip(order.paymentStatus)}`}>
                                                    {order.paymentStatus}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full uppercase ${getStsChip(order.status)}`}>
                                                    {order.status}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="text-sm text-blue-600">
                                                    {moment(order.createdAt).format('DD MMM, yyyy hh:mm:ss A')}
                                                </div>
                                            </td>
                                        </tr>
                                    })
                                }
                                {
                                    docs.length === 0 && <tr className="cursor-pointer hover:bg-gray-100">
                                        <td className="px-6 py-4 whitespace-nowrap" colSpan="8">
                                            <div className="text-sm text-red-600 text-center">
                                                No data found.
                                            </div>
                                        </td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                        <div className='flex py-2 px-4 items-center bg-blue-50'>
                            <p className='text-sm font-serif'>Show rows per page</p>
                            <select className='text-sm ml-2 bg-gray-100' name="pageSize" onChange={handleOnChange} value={pageSize}>
                                <option value={10}>10</option>
                                <option value={20}>20</option>
                                <option value={40}>40</option>
                                <option value={50}>50</option>
                            </select>
                            <div className='flex-grow'></div>
                            <p className='text-sm font-serif mr-2'>Page {pageCurrent} of {pageTotal}</p>
                            <div onClick={() => { prevPage() }}><i className={`las la-angle-left w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 ${pageCurrent <= 1 ? 'text-gray-400 cursor-not-allowed' : 'text-black cursor-pointer'}`}></i></div>
                            <div onClick={() => { nextPage() }}><i className={`las la-angle-right w-6 h-6 flex items-center justify-center rounded-full hover:bg-gray-200 ml-2 text-black ${pageCurrent === pageTotal ? 'text-gray-400 cursor-not-allowed' : 'text-black cursor-pointer'}`}></i></div>
                        </div>
                    </div>
                </div>
            </ScrollArea>
        </>
    )
}